<template>
  <div
    class="col-span-6 sm:col-span-3 xl:col-span-2 flex flex-col justify-end items-center"
    v-show="loading"
  >
    <LoadingIcon icon="three-dots" class="w-8 h-8" />
  </div>
  <div v-if="!loading">
    <div class="intro-y flex flex-col sm:flex-row items-center mt-8">
      <MessageSquareIcon />
      <h2 class="text-lg font-medium mr-auto">{{ $t('news.detail') }}</h2>
      <div class="w-full sm:w-auto flex mt-4 sm:mt-0">
        <router-link
          :to="{ name: 'superadmin-tools-news' }"
          tag="a"
          class="btn shadow-md flex items-center"
        >
          <ArrowLeftCircleIcon class="w-4 h-4 mr-2" /> {{ $t('common.back') }}
        </router-link>
      </div>
    </div>
    <div class="intro-y box px-5 pt-5 mt-5 pb-5 items-center">
      <div
        class="flex flex-col lg:flex-row border-b border-gray-200 dark:border-dark-5 pb-5 -mx-5"
      >
        <div class="flex flex-1 px-5 items-center justify-center">
          <img
            :src="news.imageWide"
            class="center"
            v-if="news.imageWide"
            style="max-width: 1264px; max-height: 300px"
          />
        </div>
      </div>
    </div>
    <div class="intro-y box px-5 pt-5 mt-5">
      <div
        class="flex flex-col lg:flex-row border-b border-gray-200 dark:border-dark-5 pb-5 -mx-5"
      >
        <div class="flex flex-1 px-5 items-center justify-center lg:justify-start">
          <div
            class="w-20 h-20 sm:w-24 sm:h-24 flex-none lg:w-32 lg:h-32 image-fit relative"
          >
            <img
              alt="Service image"
              class="rounded-full"
              :src="news.image"
              v-if="news.image"
            />
          </div>
          <div class="ml-5">
            <div class="w-24 sm:w-40 truncate sm:whitespace-normal font-medium text-lg">
              {{ news.header.sk_SK }}
            </div>
            <div class="text-gray-600">
              {{ news.perex.sk_SK }}
            </div>
          </div>
        </div>
        <div
          class="mt-6 lg:mt-0 flex-1 dark:text-gray-300 px-5 border-l border-r border-gray-200 dark:border-dark-5 border-t lg:border-t-0 pt-5 lg:pt-0"
        >
          <div class="font-medium text-center lg:text-left lg:mt-3">
            {{ $t('common.detail') }}
          </div>
          <div class="flex flex-col justify-center items-center lg:items-start mt-4">
            <div class="truncate sm:whitespace-normal flex items-center mt-3">
              <CheckCircleIcon class="w-4 h-4 mr-2" v-if="news.active" />
              <CircleIcon class="w-4 h-4 mr-2" v-if="!news.active" />
              {{ $t('common.activeShe') }}
              <div
                v-if="news.active && news.visibleFrom !== null && news.visibleTo === null"
              >
                &nbsp;- {{ formatDate(news.visibleFrom) }} &lt;
              </div>
              <div
                v-if="news.active && news.visibleFrom === null && news.visibleTo !== null"
              >
                &nbsp;- {{ formatDate(news.visibleTo) }} &gt;
              </div>
              <div
                v-if="news.active && news.visibleFrom !== null && news.visibleTo !== null"
              >
                &nbsp;-
                {{ formatDate(news.visibleFrom) + ' - ' + formatDate(news.visibleTo) }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="intro-y box px-5 pt-5 mt-5">
      <div
        class="flex flex-col lg:flex-row border-b border-gray-200 dark:border-dark-5 pb-5 -mx-5"
      >
        <div
          class="mt-6 lg:mt-0 flex-1 dark:text-gray-300 px-5 border-l border-r border-gray-200 dark:border-dark-5 border-t lg:border-t-0 pt-5 lg:pt-0"
        >
          <div class="font-medium text-center lg:text-left lg:mt-3">
            <country-flag country="sk" size="small" /> {{ news.header.sk_SK }}
          </div>
          <div class="flex flex-col justify-center items-center lg:items-start mt-4">
            <div class="truncate sm:whitespace-normal flex items-center">
              <HashIcon class="w-4 h-4 mr-2" /> {{ news.perex.sk_SK }}
            </div>
            <div class="truncate sm:whitespace-normal flex items-center mt-5">
              {{ news.body.sk_SK }}
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="intro-y box px-5 pt-5 mt-5">
      <div
        class="flex flex-col lg:flex-row border-b border-gray-200 dark:border-dark-5 pb-5 -mx-5"
      >
        <div
          class="mt-6 lg:mt-0 flex-1 dark:text-gray-300 px-5 border-l border-r border-gray-200 dark:border-dark-5 border-t lg:border-t-0 pt-5 lg:pt-0"
        >
          <div class="font-medium text-center lg:text-left lg:mt-3">
            <country-flag country="gb" size="small" /> {{ news.header.en_US }}
          </div>
          <div class="flex flex-col justify-center items-center lg:items-start mt-4">
            <div class="truncate sm:whitespace-normal flex items-center">
              <HashIcon class="w-4 h-4 mr-2" /> {{ news.perex.en_US }}
            </div>
            <div class="truncate sm:whitespace-normal flex items-center mt-5">
              {{ news.body.en_US }}
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="intro-y box px-5 pt-5 mt-5">
      <div
        class="flex flex-col lg:flex-row border-b border-gray-200 dark:border-dark-5 pb-5 -mx-5"
      >
        <div
          class="mt-6 lg:mt-0 flex-1 dark:text-gray-300 px-5 border-l border-r border-gray-200 dark:border-dark-5 border-t lg:border-t-0 pt-5 lg:pt-0"
        >
          <div class="font-medium text-center lg:text-left lg:mt-3">
            <country-flag country="cz" size="small" /> {{ news.header.cs_CZ }}
          </div>
          <div class="flex flex-col justify-center items-center lg:items-start mt-4">
            <div class="truncate sm:whitespace-normal flex items-center">
              <HashIcon class="w-4 h-4 mr-2" /> {{ news.perex.cs_CZ }}
            </div>
            <div class="truncate sm:whitespace-normal flex items-center mt-5">
              {{ news.body.cs_CZ }}
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="intro-y box px-5 pt-5 mt-5">
      <div
        class="flex flex-col lg:flex-row border-b border-gray-200 dark:border-dark-5 pb-5 -mx-5"
      >
        <div
          class="mt-6 lg:mt-0 flex-1 dark:text-gray-300 px-5 border-l border-r border-gray-200 dark:border-dark-5 border-t lg:border-t-0 pt-5 lg:pt-0"
        >
          <div class="font-medium text-center lg:text-left lg:mt-3">
            <country-flag country="de" size="small" /> {{ news.header.de_DE }}
          </div>
          <div class="flex flex-col justify-center items-center lg:items-start mt-4">
            <div class="truncate sm:whitespace-normal flex items-center">
              <HashIcon class="w-4 h-4 mr-2" /> {{ news.perex.de_DE }}
            </div>
            <div class="truncate sm:whitespace-normal flex items-center mt-5">
              {{ news.body.de_DE }}
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="intro-y box px-5 pt-5 mt-5">
      <div
        class="flex flex-col lg:flex-row border-b border-gray-200 dark:border-dark-5 pb-5 -mx-5"
      >
        <div
          class="mt-6 lg:mt-0 flex-1 dark:text-gray-300 px-5 border-l border-r border-gray-200 dark:border-dark-5 border-t lg:border-t-0 pt-5 lg:pt-0"
        >
          <div class="font-medium text-center lg:text-left lg:mt-3">
            <country-flag country="pl" size="small" /> {{ news.header.pl_PL }}
          </div>
          <div class="flex flex-col justify-center items-center lg:items-start mt-4">
            <div class="truncate sm:whitespace-normal flex items-center">
              <HashIcon class="w-4 h-4 mr-2" /> {{ news.perex.pl_PL }}
            </div>
            <div class="truncate sm:whitespace-normal flex items-center mt-5">
              {{ news.body.pl_PL }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <!-- BEGIN: Failed Notification Content -->
  <div id="failed-load-notification-content" class="toastify-content hidden flex">
    <XCircleIcon class="text-theme-6" />
    <div class="ml-4 mr-4">
      <div class="font-medium">{{ $t('news.notLoaded') }}!</div>
      <!-- <div class="text-gray-600 mt-1">Please check the fileld form.</div> -->
    </div>
  </div>
  <!-- END: Failed Notification Content -->
</template>

<script>
import { defineComponent, ref } from 'vue'
import Toastify from 'toastify-js'
import newsApi from '../../services/news/news.api'
import { HashIcon } from '@zhuowenli/vue-feather-icons'

export default defineComponent({
  computed: {
    console: () => console
  },
  data() {
    return {
      news: {}
    }
  },
  methods: {
    formatDate(date) {
      const d = new Date(date)
      return (
        d.toISOString().replace(/^(\d+)-(\d+)-(\d+).*$/, '$3.$2.$1') +
        ' ' +
        d.toLocaleTimeString().replace(/^(\d+):(\d+):\d+$/, '$1:$2')
      )
    }
  },
  mounted() {
    newsApi.getDetail(this.$route.params.uuid).then(
      (response) => {
        this.news = response.data
        this.loading = false
      },
      (error) => {
        console.log(error)
        Toastify({
          node: cash('#failed-load-notification-content')
            .clone()
            .removeClass('hidden')[0],
          duration: 10000,
          newWindow: true,
          close: true,
          gravity: 'top',
          position: 'right',
          stopOnFocus: true
        }).showToast()
        this.$router.push({ name: 'superadmin-tools-news' })
      }
    )
  },
  setup() {
    const loading = ref(true)
    return {
      loading
    }
  },
  components: { HashIcon }
})
</script>
